import { Divider } from '@joor/design-system'

import {
  AG_GRID_FILTER_FORMAT,
  endOfDate,
  formatDate,
  isSameDate,
  startOfDate,
  subtractDateUnit,
} from 'utils/dates'

import {
  AgGridFilterComparison,
  AgGridFilterType,
  AgGridFiltersTypes,
  DateFilter,
  FilterOptionProps,
} from '../../agGridFilters.types'
import {
  DatePickerFilter,
  DatePickerType,
} from '../DatePickerFilter/DatePickerFilter'
import { DateQuickFilterOption } from './DateQuickFilterOption'

export function DateMenuOptions({
  name,
  onUpdateFilters,
  value,
  onRemoveFilter,
}: FilterOptionProps & {
  value?: DateFilter
  onRemoveFilter: (name: string) => void
}) {
  const handleClick = ({
    type,
    isSelected,
  }: {
    type: string
    isSelected: boolean
  }) => {
    if (isSelected) {
      onRemoveFilter(name)
      return
    }
    const filter = getDateFilter(type)
    onUpdateFilters(name, filter)
  }

  const handleCustomRangeChange = (date: Date | Date[]) => {
    if (Array.isArray(date)) {
      const from = date[0]
      const to = date.length === 2 ? date[1] : null
      // Specific date search in agGrid tries to match the hour too, so we set it to 0
      // This makes that none of the boundaries of the search are taken into account,
      // We widen the search one day before and after so both boundaries are included.
      to && to.setDate(to.getDate() + 1)

      onUpdateFilters(name, {
        dateFrom: formatDate(from, AG_GRID_FILTER_FORMAT),
        dateTo: formatDate(to, AG_GRID_FILTER_FORMAT),
        filterType: AgGridFilterType.Date,
        type: AgGridFilterComparison.InRange,
      })
    }
  }

  const {
    isCurrentWeekSelected,
    isCurrentMonthSelected,
    isLastWeekSelected,
    isLastMonthSelected,
  } = getSelectedQuickFilter(value)

  return (
    <>
      <DateQuickFilterOption
        type={DateQuickFilter.CurrentWeek}
        isSelected={isCurrentWeekSelected}
        handleClick={handleClick}
        label="Current Week"
      />
      <DateQuickFilterOption
        type={DateQuickFilter.CurrentMonth}
        isSelected={isCurrentMonthSelected}
        handleClick={handleClick}
        label="Current Month"
      />
      <DateQuickFilterOption
        type={DateQuickFilter.LastWeek}
        isSelected={isLastWeekSelected}
        handleClick={handleClick}
        label="Last Week"
      />
      <DateQuickFilterOption
        type={DateQuickFilter.LastMonth}
        isSelected={isLastMonthSelected}
        handleClick={handleClick}
        label="Last Month"
      />
      <Divider />
      <DatePickerFilter
        type={DatePickerType.Range}
        onChange={handleCustomRangeChange}
      />
    </>
  )
}

export const DateQuickFilter = {
  CurrentWeek: 'current-week',
  CurrentMonth: 'current-month',
  LastWeek: 'last-week',
  LastMonth: 'last-month',
} as const

export type DateQuickFilterType =
  (typeof DateQuickFilter)[keyof typeof DateQuickFilter]

export const getDateFilter = (dateQuickFilter: string) => {
  const now = new Date()
  const filter: AgGridFiltersTypes = {
    dateFrom: '',
    dateTo: '',
    filterType: AgGridFilterType.Date,
    type: AgGridFilterComparison.InRange,
  }
  switch (dateQuickFilter) {
    case DateQuickFilter.CurrentWeek:
      return {
        ...filter,
        dateFrom: formatDate(startOfDate(now, 'week')),
        dateTo: formatDate(endOfDate(now, 'week')),
      }
    case DateQuickFilter.CurrentMonth:
      return {
        ...filter,
        dateFrom: formatDate(startOfDate(now, 'month')),
        dateTo: formatDate(endOfDate(now, 'month')),
      }
    case DateQuickFilter.LastWeek:
      return {
        ...filter,
        dateFrom: formatDate(
          startOfDate(subtractDateUnit(now, 1, 'weeks'), 'week'),
        ),
        dateTo: formatDate(
          endOfDate(subtractDateUnit(now, 1, 'weeks'), 'week'),
        ),
      }
    case DateQuickFilter.LastMonth:
      return {
        ...filter,
        dateFrom: formatDate(
          startOfDate(subtractDateUnit(now, 1, 'months'), 'month'),
        ),
        dateTo: formatDate(
          endOfDate(subtractDateUnit(now, 1, 'months'), 'month'),
        ),
      }
    default:
      return filter
  }
}

export const isRangeOfType = ({
  when,
  period,
  dateFrom,
  dateTo,
}: {
  when: 'current' | 'last'
  period: 'week' | 'month'
  dateFrom?: string
  dateTo?: string
}) => {
  if (!dateFrom || !dateTo) return false

  let startOfPeriod = ''
  let endOfPeriod = ''
  const now = new Date()

  switch (when) {
    case 'current':
      startOfPeriod = formatDate(startOfDate(now, period))
      endOfPeriod = formatDate(endOfDate(now, period))
      break
    case 'last':
      startOfPeriod = formatDate(
        startOfDate(subtractDateUnit(now, 1, `${period}s`), period),
      )
      endOfPeriod = formatDate(
        endOfDate(subtractDateUnit(now, 1, `${period}s`), period),
      )
      break
  }

  return isSameDate(dateFrom, startOfPeriod) && isSameDate(dateTo, endOfPeriod)
}

export const getSelectedQuickFilter = (value?: DateFilter) => {
  return {
    isCurrentWeekSelected: isRangeOfType({
      when: 'current',
      period: 'week',
      dateFrom: value?.dateFrom,
      dateTo: value?.dateTo,
    }),
    isCurrentMonthSelected: isRangeOfType({
      when: 'current',
      period: 'month',
      dateFrom: value?.dateFrom,
      dateTo: value?.dateTo,
    }),
    isLastWeekSelected: isRangeOfType({
      when: 'last',
      period: 'week',
      dateFrom: value?.dateFrom,
      dateTo: value?.dateTo,
    }),
    isLastMonthSelected: isRangeOfType({
      when: 'last',
      period: 'month',
      dateFrom: value?.dateFrom,
      dateTo: value?.dateTo,
    }),
  }
}

export const getDatePillText = (filter: DateFilter) => {
  const {
    isCurrentWeekSelected,
    isCurrentMonthSelected,
    isLastWeekSelected,
    isLastMonthSelected,
  } = getSelectedQuickFilter(filter)

  if (isCurrentWeekSelected) return 'Current Week'
  if (isCurrentMonthSelected) return 'Current Month'
  if (isLastWeekSelected) return 'Last Week'
  if (isLastMonthSelected) return 'Last Month'

  const isRange = filter.type === AgGridFilterComparison.InRange
  return `${filter.dateFrom.split(' ')[0]}${
    isRange && !!filter.dateTo ? ` to  ${filter.dateTo.split(' ')[0]}` : ''
  }`
}
